// Router de pantallas — 6 pasos.
//
// Animación: slide horizontal de 300 ms con `translateX`. Avanzando entra
// desde la derecha; retrocediendo, desde la izquierda. La animación respeta
// `prefers-reduced-motion` (definido en theme.css).
//
// API:
//   initRouter(host, { totalSteps })  — monta el primer paso
//   navigateTo(step)                  — ir a un paso concreto
//   next()                            — avanzar uno
//   back()                            — retroceder uno

import { state, update } from '/src/shared/state.js';
import { renderStep1 } from '/src/ui/steps/step1-room.js';
import { renderStep2 } from '/src/ui/steps/step2-product.js';
import { renderStep3 } from '/src/ui/steps/step3-replanteo.js';
import { renderStep4 } from '/src/ui/steps/step4-results.js';
import { renderStep5 } from '/src/ui/steps/step5-form.js';
import { renderStep6 } from '/src/ui/steps/step6-confirm.js';

const STEP_RENDERERS = {
  1: renderStep1,
  2: renderStep2,
  3: renderStep3,
  4: renderStep4,
  5: renderStep5,
  6: renderStep6,
};

const SLIDE_MS = 300;

let host = null;
let totalSteps = 6;
let currentEl = null;
let isAnimating = false;

export function initRouter(stepHost, { totalSteps: total = 6 } = {}) {
  host = stepHost;
  totalSteps = total;
  // Forzamos paso 1 al arrancar (state.js ya borra `step` al rehidratar).
  if (!state.step || state.step < 1 || state.step > totalSteps) {
    update({ step: 1 });
  }
  mountStep(state.step, /* direction */ 0);
}

export function navigateTo(step, { direction } = {}) {
  if (isAnimating) return;
  if (step < 1 || step > totalSteps) return;
  const dir = typeof direction === 'number' ? direction : Math.sign(step - state.step);
  update({ step });
  mountStep(step, dir);
}

export function next() {
  if (state.step < totalSteps) navigateTo(state.step + 1, { direction: 1 });
}

export function back() {
  if (state.step > 1) navigateTo(state.step - 1, { direction: -1 });
}

function mountStep(step, direction) {
  const renderer = STEP_RENDERERS[step];
  if (!renderer || !host) return;

  isAnimating = true;

  // Construye el nuevo paso fuera del DOM y lo posiciona inicial.
  const nextEl = document.createElement('section');
  nextEl.className = 'step';
  nextEl.setAttribute('data-step', String(step));
  nextEl.setAttribute('aria-labelledby', `step-${step}-title`);

  // Render del contenido. Cada step puede devolver una función de cleanup.
  const cleanup = renderer(nextEl, { next, back, navigateTo });
  if (typeof cleanup === 'function') {
    nextEl._cleanup = cleanup;
  }

  if (direction > 0) {
    nextEl.classList.add('step--enter-right');
  } else if (direction < 0) {
    nextEl.classList.add('step--enter-left');
  } else {
    nextEl.classList.add('step--active');
  }

  host.appendChild(nextEl);

  if (direction === 0 || !currentEl) {
    // Sin animación inicial.
    nextEl.classList.remove('step--enter-right', 'step--enter-left');
    nextEl.classList.add('step--active');
    finishMount(nextEl);
    return;
  }

  // Forzar reflow para que la transición arranque desde la posición inicial.
  void nextEl.offsetWidth;

  // Animar salida del actual.
  currentEl.classList.remove('step--active');
  currentEl.classList.add(direction > 0 ? 'step--leave-left' : 'step--leave-right');

  // Animar entrada del nuevo.
  nextEl.classList.remove('step--enter-right', 'step--enter-left');
  nextEl.classList.add('step--active');

  const oldEl = currentEl;
  setTimeout(() => {
    if (oldEl && oldEl.parentNode) {
      if (typeof oldEl._cleanup === 'function') {
        try {
          oldEl._cleanup();
        } catch (err) {
          console.warn('[router] cleanup falló:', err);
        }
      }
      oldEl.parentNode.removeChild(oldEl);
    }
    finishMount(nextEl);
  }, SLIDE_MS + 20);
}

function finishMount(el) {
  currentEl = el;
  isAnimating = false;
  // Llevar el foco al título del paso para que lectores de pantalla lo anuncien.
  const title = el.querySelector('.step__title');
  if (title) {
    title.setAttribute('tabindex', '-1');
    title.focus({ preventScroll: false });
  }
  // Reset scroll del shell.
  if (host && host.scrollTo) host.scrollTo({ top: 0, behavior: 'instant' });
  window.scrollTo({ top: 0, behavior: 'instant' });
}
